import * as i18n from 'i18next';
import { makeNewContainer } from 'ui/editor/makeNewContainer';
import { makeNewAsset } from 'ui/editor/makeNewAsset';
import { LogoAssetProperties, DeviceType, AssetType } from 'shared/models';
import { calcMinPITextHeight } from 'logic/calcMinPITextHeight';
import { keyBy } from 'lodash';

export function makePIContainers(width: number, height: number, deviceType: DeviceType | null, isNorthAmerica: boolean, t: i18n.TFunction) {
    const margin = .025;
    const smallHeight = width >= height ? .11 : .07;
    const largeHeight = width >= height ? .6 : .4;
    const smallWidth = width >= height ? .3 : .4;
    const largeWidth = width >= height ? .5 : .7;
    const logoSize = .2;

    const minTextHeight = calcMinPITextHeight(deviceType, height > width);
    const floorMinTextHeight = calcMinPITextHeight(deviceType, height > width, isNorthAmerica, true);

    const maxWeightAsset = makeNewAsset(AssetType.MaxWeight, width, height, minTextHeight);
    const maxWeightContainer = makeNewContainer({
        top: margin,
        left: margin,
        width: largeWidth,
        height: Math.max(minTextHeight, smallHeight),
        displayName: t('THEME_EDITOR.PI.MAX_WEIGHT_AREA_NAME'),
        type: 'piText',
        assetOrder: [ maxWeightAsset.id ],
        visible: !isNorthAmerica,
    });

    const maxPersonsAsset = makeNewAsset(AssetType.MaxPersons, width, height, minTextHeight);
    const maxPersonsContainer = makeNewContainer({
        top: margin,
        left: 1 - margin - largeWidth,
        width: largeWidth,
        height: Math.max(minTextHeight, smallHeight),
        displayName: t('THEME_EDITOR.PI.MAX_PERSONS_AREA_NAME'),
        type: 'piText',
        assetOrder: [ maxPersonsAsset.id ],
        visible: !isNorthAmerica,
    });

    const elevatorIdAsset = makeNewAsset(AssetType.ElevatorId, width, height, minTextHeight);
    const elevatorIdContainer = makeNewContainer({
        top: 1 - margin - smallHeight,
        left: (1 - margin * 2 - smallWidth) / 2 + margin,
        width: smallWidth,
        height: Math.max(minTextHeight, smallHeight),
        displayName: t('THEME_EDITOR.PI.ELEVATOR_ID_AREA_NAME'),
        type: 'piText',
        assetOrder: [ elevatorIdAsset.id ],
        visible: !isNorthAmerica,
    });

    // Calculate a square for the direction container so that the direction indicators overlap
    const squareWidth = Math.min(largeHeight, largeWidth * .3333);
    const squareHeight = squareWidth * width / height;

    const directionAsset = makeNewAsset(AssetType.Direction, width, height);
    const directionContainer = makeNewContainer({
        top: (1 - margin * 2 - squareHeight) / 2 + margin,
        left: ((1 - largeWidth) / 2) + ((largeWidth * .3333 - squareWidth) / 2),
        width: squareWidth,
        height: squareHeight,
        displayName: t('THEME_EDITOR.PI.DIRECTION_AREA_NAME'),
        type: 'direction',
        assetOrder: [ directionAsset.id ],
    });

    const floorAsset = makeNewAsset(AssetType.Floor, width, height, 0, 0, floorMinTextHeight);
    const floorContainer = makeNewContainer({
        top: (1 - margin * 2 - largeHeight) / 2 + margin,
        left: 1 - (1 - largeWidth) / 2 - (largeWidth * .6666),
        width: largeWidth * .6666,
        height: Math.max(floorMinTextHeight, largeHeight),
        displayName: t('THEME_EDITOR.PI.FLOOR_AREA_NAME'),
        type: 'floor',
        assetOrder: [ floorAsset.id ],
    });

    const noSmokingLogoAsset = makeNewAsset(AssetType.Logo, width, height);
    (noSmokingLogoAsset.properties as LogoAssetProperties).logo = 'no-smoking';
    const noSmokingLogoContainer = makeNewContainer({
        top: 1 - margin - logoSize,
        left: 1 - margin - logoSize,
        width: logoSize,
        height: logoSize,
        displayName: t('THEME_EDITOR.PI.LOGO.NO_SMOKING'),
        type: 'logo',
        assetOrder: [ noSmokingLogoAsset.id ],
        visible: !isNorthAmerica,
    });

    // Calvin wants Floor Arrival to show up on the toolbar and be explicitly created by users
    //const floorArrivalMessageContainer = makeNewContainer({
    //    top: 0,
    //    left: 0,
    //    width: 1,
    //    height: 1,
    //    displayName: t('THEME_EDITOR.PI.FLOOR_ARRIVAL_AREA_NAME'),
    //    type: 'floorArrival',
    //    assetOrder: [],
    //});

    return {
        piContainers: [
            noSmokingLogoContainer,
            maxWeightContainer,
            maxPersonsContainer,
            elevatorIdContainer,
            //floorArrivalMessageContainer,
            directionContainer,
            floorContainer,
        ],
        piAssets: keyBy([
            maxWeightAsset,
            maxPersonsAsset,
            elevatorIdAsset,
            directionAsset,
            floorAsset,
            noSmokingLogoAsset,
        ], 'id'),
    };
}
